// src/views/dashboard/kyc/KYCManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Tabs,
  Tab,
  Button,
  Offcanvas,
  Modal,
  Form,
  Badge,
  Spinner,
  Alert,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import {
  getDoneVendorKyc,
  updateVendorKycStatus,
  getDoneEquipmentKyc,
  updateEquipmentKycStatus,
} from "../../../services/service";
import { toast } from "react-toastify";

/* Colour helper for badges */
const badgeColor = {
  done: "bg-primary",
  approved: "bg-success",
  rejected: "bg-danger",
  pending: "bg-warning",
};

// SAFE BILINGUAL TEXT EXTRACTOR (ADDED)
const getBilingualText = (obj, field) => {
  if (!obj || typeof obj !== "object") return "N/A";
  const value = obj[field];
  if (!value || typeof value !== "object")
    return (value || "").toString().trim() || "N/A";
  const en = value.en?.trim() || "";
  const hi = value.hi?.trim() || "";
  if (!en && !hi) return "N/A";
  if (en && hi) return `${en} (${hi})`;
  return en || hi;
};

const KYCManager = () => {
  /* 1 Tabs state – default to "equipments" */
  const [key, setKey] = useState("equipments");

  /* 2 Data states */
  const [vendorQueue, setVendorQueue] = useState([]);
  const [equipQueue, setEquipQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination Metadata
  const [totalPagesVendors, setTotalPagesVendors] = useState(0);
  const [totalPagesEquip, setTotalPagesEquip] = useState(0);
  const [totalVendors, setTotalVendors] = useState(0);
  const [totalEquip, setTotalEquip] = useState(0);

  /* 3 Offcanvas for document preview */
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasData, setCanvasData] = useState({
    docs: [],
    cursor: 0,
    title: "",
  });
  const [VechicleData, setVechicleData] = useState({
    docs: [],
    cursor: 0,
    title: "",
  });
  const [showVechicle, setShowVechile] = useState(false);

  /* 4 Modal for Approve/Reject (equipment/vendor-specific) */
  const [modal, setModal] = useState({
    show: false,
    action: "",
    item: null,
    isVendor: false,
    reason: { en: "", hi: "" },
  });

  /* 5 Separate Modal for Vendor Detail/KYC (on Equipments tab) */
  const [vendorModal, setVendorModal] = useState({
    show: false,
    vendor: null,
    pendingEquipmentApproval: null, // NEW: Track pending equipment approval
  });

  //  Search & Filter States (Separated)
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorStatus, setVendorStatus] = useState("all");

  const [equipSearch, setEquipSearch] = useState("");
  const [equipStatus, setEquipStatus] = useState("all");

  // ───── Pagination State ─────
  const [currentPageVendors, setCurrentPageVendors] = useState(1);
  const [currentPageEquip, setCurrentPageEquip] = useState(1);
  const itemsPerPage = 10; // change as needed

  // Debounce Search
  const [debouncedVendorSearch, setDebouncedVendorSearch] =
    useState(vendorSearch);
  const [debouncedEquipSearch, setDebouncedEquipSearch] = useState(equipSearch);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedVendorSearch(vendorSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [vendorSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEquipSearch(equipSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [equipSearch]);

  // ───── Pagination Component ─────
  const renderPagination = (totalPages, currentPage, setPage) => {
    if (totalPages <= 1) return null;

    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.First
          onClick={() => setPage(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => setPage(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
        />

        {/* Show first page if not in window */}
        {currentPage > 3 && (
          <>
            <Pagination.Item onClick={() => setPage(1)}>1</Pagination.Item>
            {currentPage > 4 && <Pagination.Ellipsis disabled />}
          </>
        )}

        {/* Window of pages around current page */}
        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          if (page >= currentPage - 2 && page <= currentPage + 2) {
            return (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => setPage(page)}
              >
                {page}
              </Pagination.Item>
            );
          }
          return null;
        })}

        {/* Show last page if not in window */}
        {currentPage < totalPages - 2 && (
          <>
            {currentPage < totalPages - 3 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => setPage(totalPages)}>
              {totalPages}
            </Pagination.Item>
          </>
        )}

        <Pagination.Next
          onClick={() => setPage(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setPage(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  useEffect(() => {
    setCurrentPageVendors(1);
  }, [debouncedVendorSearch, vendorStatus]);

  useEffect(() => {
    setCurrentPageEquip(1);
  }, [debouncedEquipSearch, equipStatus]);

  /* ───── FETCH DATA ───── */
  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDoneVendorKyc({
        page: currentPageVendors,
        limit: itemsPerPage,
        search: debouncedVendorSearch,
        status: vendorStatus === "all" ? "" : vendorStatus,
      });

      const transformedVendors = response.data
        .filter((item) => item.vendor !== null)
        .map((item) => ({
          id: item._id,
          name: getBilingualText(item.vendor, "name"),
          phone: item.vendor?.phone || "N/A",
          email: item.vendor?.email || "N/A",
          location: "N/A",
          submitted: new Date(item.createdAt).toLocaleString(),
          pendingDocs: item.status === "pending" ? 0 : 1,
          status: item.status,
          docs: [
            ...(item.aadhaar_front_url
              ? [{ name: "Aadhaar Card (Front)", url: item.aadhaar_front_url }]
              : []),
            ...(item.aadhaar_back_url
              ? [{ name: "Aadhaar Card (Back)", url: item.aadhaar_back_url }]
              : []),
            ...(item.driving_license_front_url
              ? [
                {
                  name: "Driving License (Front)",
                  url: item.driving_license_front_url,
                },
              ]
              : []),
          ],
          originalData: item,
        }));

      setVendorQueue(transformedVendors);
      setTotalPagesVendors(response.pages || 1);
      setTotalVendors(response.total || 0);
    } catch (err) {
      console.error("Fetch vendors error:", err);
      // setError("Failed to fetch vendors"); // Optional: don't block UI
    } finally {
      setLoading(false);
    }
  }, [currentPageVendors, debouncedVendorSearch, vendorStatus]);

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDoneEquipmentKyc({
        page: currentPageEquip,
        limit: itemsPerPage,
        search: debouncedEquipSearch,
        status: equipStatus === "all" ? "" : equipStatus,
      });

      const transformedEquipment = response.data
        .filter(
          (item) => item.equipment !== null && item.equipment.vendor !== null
        )
        .map((item) => ({
          id: item._id,
          name: getBilingualText(item.equipment?.category, "name"),
          vendor: getBilingualText(item.equipment?.vendor, "name"),
          vendorId: item.equipment?.vendor?._id || null,
          submitted: new Date(item.createdAt).toLocaleString(),
          pendingDocs: item.status === "pending" ? 0 : 1,
          status: item.status,
          docs: [
            ...(item.rc_front_url
              ? [
                {
                  name: "RC Front",
                  url: item.rc_front_url,
                  rc_number: item.rc_number || null,
                },
              ]
              : []),
            ...(item.rc_back_url
              ? [
                {
                  name: "RC Back",
                  url: item.rc_back_url,
                  rc_number: item.rc_number || null,
                },
              ]
              : []),
            ...(item.insurance_url
              ? [
                {
                  name: "Insurance",
                  url: item.insurance_url,
                  insurance_number: item.insurance_number || null,
                },
              ]
              : []),
            ...(item.pollution_certificate_url
              ? [
                {
                  name: "Pollution Certificate",
                  url: item.pollution_certificate_url,
                  pollution_up_to_date: item.pollution_up_to_date || null,
                },
              ]
              : []),
          ],
          originalData: item,
        }));

      setEquipQueue(transformedEquipment);
      setTotalPagesEquip(response.pages || 1);
      setTotalEquip(response.total || 0);
    } catch (err) {
      console.error("Fetch equipment error:", err);
      // setError("Failed to fetch equipment");
    } finally {
      setLoading(false);
    }
  }, [currentPageEquip, debouncedEquipSearch, equipStatus]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const fetchData = async () => {
    // Wrapper to refresh both manually if needed
    await Promise.all([fetchVendors(), fetchEquipment()]);
  };

  /* ───── HANDLE "VIEW DOCUMENTS" (Offcanvas) ───── */
  const handleView = (item) => {
    setCanvasData({ docs: item.docs, cursor: 0, title: item.name });
    setShowCanvas(true);
  };
  const handleVechicleImage = (item) => {
    setVechicleData({
      docs: item?.originalData?.equipment?.photos,
      cursor: 0,
      title: "Vehicle Image",
    });
    setShowVechile(true);
  };

  /* ───── HANDLE Approve / Reject ───── */
  const handleAction = (action, item, isVendor = false) => {
    // NEW: Check if it's equipment approval and vendor is not approved
    if (!isVendor && action === "approved" && item.vendorId) {
      const vendor = vendorQueue.find(
        (v) => v.originalData.vendor?._id === item.vendorId
      );

      if (vendor && vendor.status !== "approved") {
        // Vendor is not approved, show vendor modal first
        setVendorModal({
          show: true,
          vendor: vendor,
          pendingEquipmentApproval: item, // Store the equipment to approve after vendor
        });
        return;
      }
    }

    // Normal flow for direct approvals
    setModal({
      show: true,
      action,
      item,
      isVendor,
      reason: { en: "", hi: "" },
    });
  };

  const confirmAction = async () => {
    try {
      if (modal.action === "rejected") {
        if (!modal.reason.en.trim() || !modal.reason.hi.trim()) {
          toast.error("Reason in both English & Hindi is required.");
          return;
        }
      }

      setLoading(true);

      const status =
        modal.action.toLowerCase() === "approved" ? "approved" : "rejected";

      const payload = {
        status,
        reason: status === "rejected" ? modal.reason : null,
      };
      console.log(payload, "payloadpayloadpayload");
      if (modal.isVendor) {
        await updateVendorKycStatus(
          modal.item.originalData.vendor._id,
          payload
        );
      } else {
        await updateEquipmentKycStatus(
          modal.item.originalData.equipment._id,
          payload
        );
      }

      await fetchData();
      toast.success(`KYC ${status} successfully`);

      setModal({ ...modal, show: false });
    } catch (err) {
      toast.error("Failed to update KYC status");
    } finally {
      setLoading(false);
    }
  };

  /* ───── NEW: Handle Vendor Approval from Vendor Modal ───── */
  const handleVendorApprovalFromModal = async (action) => {
    try {
      setLoading(true);
      const status = action.toLowerCase();

      // FIXED: send correct payload
      await updateVendorKycStatus(vendorModal.vendor.originalData.vendor._id, {
        status,
        reason: null,
      });

      if (status === "approved" && vendorModal.pendingEquipmentApproval) {
        // FIXED: correct payload for equipment
        await updateEquipmentKycStatus(
          vendorModal.pendingEquipmentApproval.originalData.equipment._id,
          { status: "approved", reason: null }
        );
      }

      await fetchData();

      setVendorModal({
        show: false,
        vendor: null,
        pendingEquipmentApproval: null,
      });
    } catch (err) {
      setError("Failed to update KYC status: " + err.message);
      console.error("Update error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ───── HANDLE Vendor "View" from Equipments table ───── */
  const handleViewVendorFromEquip = (vendorName) => {
    // Find that vendor object in vendorQueue:
    const v = vendorQueue.find((x) => x.name === vendorName);
    if (v) {
      setVendorModal({ show: true, vendor: v, pendingEquipmentApproval: null });
    }
  };

  /* ───── RENDER ROWS HELPER ───── */
  const renderRows = (data, type) =>
    data.map((item) => (
      <tr key={`${type}-${item.id}`}>
        <td>
          {type === "vendor" ? (
            item.name
          ) : (
            /* Make Vendor clickable on Equipments tab: */
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleViewVendorFromEquip(item.vendor)}
            >
              {item.vendor}
            </Button>
          )}
        </td>
        {type === "vendor" ? (
          <>
            <td>{item.phone}</td>
            <td>{item.email}</td>
          </>
        ) : (
          <td>{item.name}</td>
        )}
        <td>{item.submitted}</td>
        <td>
          <span className={`badge ${badgeColor[item.status]}`}>
            {item.status === "done" ? "Pending" : item.status}
          </span>
        </td>
        <td>
          {/* Actions vary based on type */}
          <div className="d-flex gap-2">
            <Button size="sm" variant="info" onClick={() => handleView(item)}>
              View Docs
            </Button>
            <Button
              size="sm"
              variant="success"
              onClick={() => handleAction("approved", item, type === "vendor")}
              disabled={
                (type !== "equip" &&
                  !(item.status === "pending" || item.status === "done")) ||
                (type === "equip" && item.status === "approved")
              }
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => handleAction("rejected", item, type === "vendor")}
              disabled={
                (type !== "equip" &&
                  !(item.status === "pending" || item.status === "done")) ||
                (type === "equip" && item.status === "rejected")
              }
            >
              Reject
            </Button>
            {type == "equip" && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleVechicleImage(item)}
              // disabled={!(item.status === "pending" || item.status === "done")}
              >
                Vehicle Image
              </Button>
            )}
          </div>
        </td>
      </tr>
    ));
  return (
    <>
      <Row className="mb-4">
        <Col sm="12">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">KYC Manager</h4>
              </div>

              {/* Search & Filter Controls */}
              <div className="d-flex justify-content-between align-items-center mb-3 px-3">
                <Form.Control
                  type="text"
                  placeholder={`Search ${key === "vendors" ? "vendors" : "equipments"
                    }...`}
                  value={key === "vendors" ? vendorSearch : equipSearch}
                  onChange={(e) =>
                    key === "vendors"
                      ? setVendorSearch(e.target.value)
                      : setEquipSearch(e.target.value)
                  }
                  style={{ maxWidth: "250px" }}
                />
                &nbsp;
                <Form.Select
                  value={key === "vendors" ? vendorStatus : equipStatus}
                  onChange={(e) =>
                    key === "vendors"
                      ? setVendorStatus(e.target.value)
                      : setEquipStatus(e.target.value)
                  }
                  style={{ maxWidth: "180px" }}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body className="px-0">
              {error && (
                <Alert
                  variant="danger"
                  className="mx-3"
                  dismissible
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              <Tabs
                id="kyc-tabs"
                activeKey={key}
                onSelect={(k) => setKey(k)}
                className="px-3"
              >
                {/* ───── Vendors TAB ───── */}
                <Tab eventKey="vendors" title="Vendors">
                  <div className="table-responsive p-3">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading vendors...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-striped mb-0">
                          <thead className="light">
                            <tr>
                              <th>Vendor</th>
                              <th>Phone</th>
                              <th>Email</th>
                              <th>Submitted</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendorQueue && vendorQueue.length > 0 ? (
                              renderRows(vendorQueue, "vendor")
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center py-4 text-muted"
                                >
                                  No vendors found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {renderPagination(
                          totalPagesVendors,
                          currentPageVendors,
                          setCurrentPageVendors
                        )}
                      </>
                    )}
                  </div>
                </Tab>

                {/* ───── Equipments TAB ───── */}
                <Tab eventKey="equipments" title="Equipments">
                  <div className="table-responsive p-3">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading equipment...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-striped mb-0">
                          <thead className="light">
                            <tr>
                              <th>Vendor</th>
                              <th>Equipment</th>
                              <th>Submitted</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {equipQueue && equipQueue.length > 0 ? (
                              renderRows(equipQueue, "equip")
                            ) : (
                              <tr>
                                <td
                                  colSpan="5"
                                  className="text-center py-4 text-muted"
                                >
                                  No equipment found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {renderPagination(
                          totalPagesEquip,
                          currentPageEquip,
                          setCurrentPageEquip
                        )}
                      </>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ────────── Offcanvas: Document Viewer ────────── */}
      <Offcanvas
        show={showCanvas}
        onHide={() => setShowCanvas(false)}
        placement="end"
        backdrop
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{canvasData.title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {canvasData.docs.length ? (
            <>
              <p className="text-muted">
                Document {canvasData.cursor + 1} / {canvasData.docs.length}
              </p>
              <div className="border rounded p-3 text-center">
                {canvasData.docs[canvasData.cursor].url ? (
                  <img
                    src={canvasData.docs[canvasData.cursor].url}
                    alt={canvasData.docs[canvasData.cursor].name}
                    style={{ maxWidth: "100%", maxHeight: "400px" }}
                    className="img-fluid"
                  />
                ) : (
                  <span className="text-secondary">
                    [ Preview of {canvasData.docs[canvasData.cursor].name} ]
                  </span>
                )}
              </div>

              {/* Additional Info */}
              <div className="mt-2 text-center">
                {canvasData.docs[canvasData.cursor].rc_number && (
                  <p>
                    <strong>RC Number:</strong>{" "}
                    {canvasData.docs[canvasData.cursor].rc_number}
                  </p>
                )}
                {canvasData.docs[canvasData.cursor].insurance_number && (
                  <p>
                    <strong>Insurance Number:</strong>{" "}
                    {canvasData.docs[canvasData.cursor].insurance_number}
                  </p>
                )}
                {canvasData.docs[canvasData.cursor].pollution_up_to_date && (
                  <p>
                    <strong>Pollution Valid Up To:</strong>{" "}
                    {new Date(
                      canvasData.docs[canvasData.cursor].pollution_up_to_date
                    ).toLocaleDateString()}
                  </p>
                )}
              </div>

              <p className="text-center mt-2 fw-bold">
                {canvasData.docs[canvasData.cursor].name}
              </p>

              <div className="d-flex justify-content-between mt-3">
                <Button
                  disabled={canvasData.cursor === 0}
                  onClick={() =>
                    setCanvasData({
                      ...canvasData,
                      cursor: canvasData.cursor - 1,
                    })
                  }
                >
                  Prev
                </Button>
                <Button
                  disabled={canvasData.cursor === canvasData.docs.length - 1}
                  onClick={() =>
                    setCanvasData({
                      ...canvasData,
                      cursor: canvasData.cursor + 1,
                    })
                  }
                >
                  Next
                </Button>
              </div>
            </>
          ) : (
            <p>No documents uploaded.</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      {/* ────────── Offcanvas: Vechicle Viewer ────────── */}
      <Offcanvas
        show={showVechicle}
        onHide={() => setShowVechile(false)}
        placement="end"
        backdrop
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{VechicleData.title}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {VechicleData.docs && VechicleData.docs.length ? (
            <>
              <p className="text-muted">
                Document {VechicleData.cursor + 1} / {VechicleData.docs.length}
              </p>

              {/* Image Preview */}
              <div className="border rounded p-3 text-center">
                <img
                  src={VechicleData.docs[VechicleData.cursor]}
                  alt={`Vehicle image ${VechicleData.cursor + 1}`}
                  className="img-fluid"
                  style={{ maxHeight: "400px", objectFit: "contain" }}
                />
              </div>

              {/* Navigation Buttons */}
              <div className="d-flex justify-content-between mt-3">
                <Button
                  variant="secondary"
                  disabled={VechicleData.cursor === 0}
                  onClick={() =>
                    setVechicleData((prev) => ({
                      ...prev,
                      cursor: prev.cursor - 1,
                    }))
                  }
                >
                  Prev
                </Button>

                <Button
                  variant="secondary"
                  disabled={
                    VechicleData.cursor === VechicleData.docs.length - 1
                  }
                  onClick={() =>
                    setVechicleData((prev) => ({
                      ...prev,
                      cursor: prev.cursor + 1,
                    }))
                  }
                >
                  Next
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center text-muted">No images uploaded.</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* ────────── Approve/Reject Modal ────────── */}
      <Modal
        show={modal.show}
        onHide={() => setModal({ ...modal, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.action} KYC – {modal.item?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modal.action === "rejected" && (
            <>
              <Form.Group>
                <Form.Label>
                  Reason (English) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modal.reason.en}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      reason: { ...modal.reason, en: e.target.value },
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Reason (Hindi) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modal.reason.hi}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      reason: { ...modal.reason, hi: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Alert variant="warning">
                Both English and Hindi reasons are required.
              </Alert>
            </>
          )}

          <p className="mt-3">
            Are you sure you want to{" "}
            <strong>
              {modal.action === "approved" ? "approve" : "reject"}
            </strong>{" "}
            this KYC?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setModal({ ...modal, show: false })}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant={modal.action === "approved" ? "success" : "danger"}
            onClick={confirmAction}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : modal.action}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ────────── Vendor Detail/KYC Modal ────────── */}
      <Modal
        show={vendorModal.show}
        onHide={() =>
          setVendorModal({
            show: false,
            vendor: null,
            pendingEquipmentApproval: null,
          })
        }
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Vendor Details – {vendorModal.vendor?.name}
            {vendorModal.pendingEquipmentApproval && (
              <Badge bg="info" className="ms-2">
                Vendor Approval Required
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {vendorModal.vendor ? (
            <>
              {vendorModal.pendingEquipmentApproval && (
                <Alert variant="warning">
                  <strong>Equipment Approval Pending:</strong> This vendor must
                  be approved first before the equipment "
                  {vendorModal.pendingEquipmentApproval.name}" can be approved.
                </Alert>
              )}

              <p>
                <strong>Name:</strong> {vendorModal.vendor.name}
              </p>
              <p>
                <strong>Phone:</strong> {vendorModal.vendor.phone}
              </p>
              <p>
                <strong>Email:</strong> {vendorModal.vendor.email}
              </p>
              <p>
                <strong>Vendor verified On:</strong>{" "}
                {vendorModal.vendor.submitted}
              </p>
              <p>
                <strong>Vendor Pending Documents:</strong>{" "}
                <Badge bg="info">{vendorModal.vendor.pendingDocs}</Badge>
              </p>
              <p>
                <strong>Vendor KYC Status:</strong>{" "}
                <span
                  className={`badge ${badgeColor[vendorModal.vendor.status]}`}
                >
                  {vendorModal.vendor.status === "pending"
                    ? "Pending"
                    : vendorModal.vendor.status}
                </span>
              </p>
              <hr />

              <h6>Uploaded Documents:</h6>
              {vendorModal.vendor.docs.length ? (
                <div className="row">
                  {vendorModal.vendor.docs.map((doc, i) => (
                    <div key={i} className="col-md-6 mb-3">
                      <div className="card">
                        <div className="card-body text-center">
                          <img
                            src={doc.url}
                            alt={doc.name}
                            style={{ maxWidth: "100%", maxHeight: "150px" }}
                            className="img-fluid mb-2"
                          />
                          <p className="small mb-0">{doc.name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No documents uploaded.</p>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setVendorModal({
                show: false,
                vendor: null,
                pendingEquipmentApproval: null,
              })
            }
          >
            Close
          </Button>

          {/* Show different buttons based on whether there's pending equipment approval */}
          {vendorModal.pendingEquipmentApproval ? (
            <>
              <Button
                variant="success"
                onClick={() => handleVendorApprovalFromModal("approved")}
                disabled={vendorModal.vendor?.status === "approved" || loading}
              >
                {loading ? <Spinner size="sm" /> : "Approve Vendor & Equipment"}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleVendorApprovalFromModal("rejected")}
                disabled={vendorModal.vendor?.status === "rejected" || loading}
              >
                {loading ? <Spinner size="sm" /> : "Reject Vendor"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="success"
                onClick={() =>
                  handleAction("approved", vendorModal.vendor, true)
                }
                disabled={vendorModal.vendor?.status === "approved"}
              >
                Approve Vendor
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  setModal({
                    show: true,
                    action: "rejected",
                    item: vendorModal.vendor,
                    isVendor: true,
                    reason: { en: "", hi: "" },
                  })
                }
                disabled={
                  vendorModal.vendor?.status === "rejected" ||
                  vendorModal.vendor?.status === "approved"
                }
              >
                Reject Vendor
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default KYCManager;
